import React, { useCallback, useEffect, useRef, useState } from 'react'
import { TextField } from '@mui/material'
import { useToasts } from 'react-toast-notifications'
import {
  GetAdvancedQuickNoteDocument,
  useGetAdvancedQuickNoteQuery,
  useUpdateAdvancedQuickNoteMutation,
} from '../api/advancedQuickNote.generated'
import { describeFailure } from '../api/failureReason'
import FailureMessage from './FailureMessage'
import { onDark } from '../theme'

const AUTOSAVE_MS = 700

type SaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'failed'

const STATUS_TEXT: Record<SaveStatus, string> = {
  idle: '',
  unsaved: '',
  saving: 'Saving…',
  saved: 'Saved',
  failed: 'Not saved',
}

// Keyed by uid at the call site: a patient switch unmounts this, and the cleanup below saves what was typed.
const QuickNoteSection = ({ uid }: { uid: string }) => {
  const { data, loading } = useGetAdvancedQuickNoteQuery({ variables: { uid } })
  const [updateQuickNote] = useUpdateAdvancedQuickNoteMutation({
    refetchQueries: [{ query: GetAdvancedQuickNoteDocument, variables: { uid } }],
    awaitRefetchQueries: true,
  })
  const { addToast } = useToasts()
  const [draft, setDraft] = useState<string | null>(null)
  const [status, setStatus] = useState<SaveStatus>('idle')
  const pending = useRef<string | null>(null)
  const saving = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const flushRef = useRef<() => void>(() => undefined)

  // One save at a time: two in flight could land out of order and leave the older text stored.
  const flush = useCallback(() => {
    clearTimeout(timer.current)
    if (pending.current === null || saving.current) {
      return
    }
    const quickNote = pending.current
    pending.current = null
    saving.current = true
    setStatus('saving')
    updateQuickNote({ variables: { uid, quickNote } }).then(
      () => {
        saving.current = false
        if (pending.current === null) {
          setDraft(null)
          setStatus('saved')
        } else {
          flushRef.current()
        }
      },
      (error: unknown) => {
        saving.current = false
        addToast(<FailureMessage failure={describeFailure('Failed to save the quick note.', error)} />, {
          appearance: 'error',
        })
        // Keep it queued so the next blur, edit or unmount tries again.
        if (pending.current === null) {
          pending.current = quickNote
          setStatus('failed')
        } else {
          flushRef.current()
        }
      }
    )
  }, [uid, updateQuickNote, addToast])

  flushRef.current = flush
  useEffect(() => () => flushRef.current(), [])

  const change = (quickNote: string) => {
    setDraft(quickNote)
    setStatus('unsaved')
    pending.current = quickNote
    clearTimeout(timer.current)
    timer.current = setTimeout(() => flushRef.current(), AUTOSAVE_MS)
  }

  return (
    <TextField
      fullWidth
      multiline
      minRows={4}
      placeholder="Quick note"
      inputProps={{ 'aria-label': 'Quick note' }}
      disabled={loading && !data}
      value={draft ?? data?.patient?.quickNote ?? ''}
      onChange={(event) => change(event.target.value)}
      onBlur={flush}
      helperText={STATUS_TEXT[status]}
      FormHelperTextProps={{ sx: { textAlign: 'right', color: onDark.secondary } }}
      sx={{
        '& .MuiOutlinedInput-root': {
          backgroundColor: onDark.surface,
          borderRadius: 1,
          color: onDark.primary,
          '& fieldset': { borderColor: onDark.line },
          '&:hover fieldset': { borderColor: onDark.line },
          '&.Mui-focused fieldset': { borderColor: onDark.primary },
        },
        '& .MuiOutlinedInput-input': {
          backgroundColor: onDark.surface,
          color: onDark.primary,
        },
        '& .MuiInputBase-input::placeholder': { color: onDark.secondary, opacity: 1 },
      }}
    />
  )
}

export default QuickNoteSection
